import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { apiRequest } from "@/helper/api";
import type { LmsMode } from "@/components/lms/lms.utils";

export type LmsDashboardQueryParams = Record<string, string | number>;
export type LmsDashboardStatusFilterParams =
  | LmsDashboardQueryParams
  | null;

export type LmsDashboardDateField =
  | "created_on"
  | "started_on"
  | "completed_on"
  | "certificate__last_downloaded_on";

export interface LmsDashboardResponse {
  company_subject: string;
  summary: {
    enrolments: {
      total: number;
      completed: number;
    };
    courses: {
      total_available: number;
      with_enrolled_users: number;
    } | null;
  };
  status_counts: {
    not_started: number;
    in_progress: number;
    completed: number;
    total: number;
  };
  latest_completions: Array<{
    id: string;
    completed_on: string | null;
    course: {
      id: string;
      name: string;
    };
    participant: {
      id: string;
      name: string;
    };
  }>;
}

interface BuildDateFilterParams {
  dateField: LmsDashboardDateField;
  dateRange: [Date | null, Date | null] | [];
}

export const buildLmsDashboardDateFilterParams = ({
  dateField,
  dateRange,
}: BuildDateFilterParams): LmsDashboardQueryParams => {
  const [start, end] = dateRange;
  const params: LmsDashboardQueryParams = {};

  if (start) {
    params[`date[${dateField}][from]`] = dayjs(start)
      .startOf("day")
      .toISOString();
  }
  if (end) {
    params[`date[${dateField}][to]`] = dayjs(end)
      .endOf("day")
      .toISOString();
  }

  return params;
};

export const areLmsDashboardFilterParamsEqual = (
  left: LmsDashboardStatusFilterParams,
  right: LmsDashboardStatusFilterParams,
) => {
  if (left === null || right === null) {
    return left === right;
  }

  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);
  return (
    leftEntries.length === rightEntries.length &&
    leftEntries.every(([key, value]) => right[key] === value)
  );
};

interface UseLmsDashboardParams {
  companySubjectId?: string;
  dateFilterParams: LmsDashboardQueryParams;
  enabled: boolean;
  mode: LmsMode;
  refreshKey: number;
  scopedRequestParameters: LmsDashboardQueryParams;
  shouldNotImpersonate: boolean;
  statusFilterParams: LmsDashboardStatusFilterParams;
}

const buildDashboardScopeKey = ({
  companySubjectId,
  mode,
  scopedRequestParameters,
  shouldNotImpersonate,
}: Pick<
  UseLmsDashboardParams,
  | "companySubjectId"
  | "mode"
  | "scopedRequestParameters"
  | "shouldNotImpersonate"
>) =>
  JSON.stringify([
    mode,
    mode === "administrator" ? companySubjectId ?? "" : "",
    shouldNotImpersonate,
    Object.entries(scopedRequestParameters).sort(([leftKey], [rightKey]) =>
      leftKey.localeCompare(rightKey),
    ),
  ]);

export function useLmsDashboard({
  companySubjectId,
  dateFilterParams,
  enabled,
  mode,
  refreshKey,
  scopedRequestParameters,
  shouldNotImpersonate,
  statusFilterParams,
}: UseLmsDashboardParams) {
  const [data, setData] = useState<LmsDashboardResponse | null>(null);
  const [dataScopeKey, setDataScopeKey] = useState<string | null>(null);
  const [dataStatusFilterParams, setDataStatusFilterParams] =
    useState<LmsDashboardStatusFilterParams>(null);
  const [error, setError] = useState<any>(null);
  const [errorScopeKey, setErrorScopeKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const clearError = useCallback(() => {
    setError(null);
    setErrorScopeKey(null);
  }, []);
  const dashboardScopeKey = buildDashboardScopeKey({
    companySubjectId,
    mode,
    scopedRequestParameters,
    shouldNotImpersonate,
  });

  useEffect(() => {
    let isMounted = true;
    const canLoad =
      enabled &&
      statusFilterParams !== null &&
      (mode !== "administrator" || Boolean(companySubjectId));

    if (!canLoad) {
      if (!enabled || (mode === "administrator" && !companySubjectId)) {
        setData(null);
        setDataScopeKey(null);
        setDataStatusFilterParams(null);
        setError(null);
        setErrorScopeKey(null);
      }
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setDataStatusFilterParams(null);
    setError(null);
    setErrorScopeKey(null);
    setIsLoading(true);

    apiRequest<LmsDashboardResponse>({
      entity: "enrolment",
      method: "get",
      additionalRouteParts: { dashboard: "/" },
      shouldNotImpersonate,
      parametersToRender: {
        ...scopedRequestParameters,
        ...(mode === "administrator" && companySubjectId
          ? { company_subject: companySubjectId }
          : {}),
        ...dateFilterParams,
        ...statusFilterParams,
      },
    })
      .then((response) => {
        if (isMounted) {
          setData(response.data);
          setDataScopeKey(dashboardScopeKey);
          setDataStatusFilterParams(statusFilterParams);
        }
      })
      .catch((requestError) => {
        if (isMounted) {
          setError(requestError);
          setErrorScopeKey(dashboardScopeKey);
          setData(null);
          setDataScopeKey(null);
          setDataStatusFilterParams(null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    companySubjectId,
    dashboardScopeKey,
    dateFilterParams,
    enabled,
    mode,
    refreshKey,
    scopedRequestParameters,
    shouldNotImpersonate,
    statusFilterParams,
  ]);

  const hasCurrentScopeData =
    enabled && dataScopeKey === dashboardScopeKey;
  const hasCurrentScopeError =
    enabled && errorScopeKey === dashboardScopeKey;

  return {
    clearError,
    data: hasCurrentScopeData ? data : null,
    dataStatusFilterParams: hasCurrentScopeData
      ? dataStatusFilterParams
      : null,
    error: hasCurrentScopeError ? error : null,
    isLoading,
  };
}
